import { cn } from "@/shared/lib/utils/cn";
import { Skeleton } from "@/shared/ui/shadcn-components/skeleton";

/** Config / detail section card with header + rows. */
export function SkeletonConfigCard({ rows = 3, className }: { rows?: number; className?: string }) {
  return (
    <div
      role="status"
      aria-label="Loading"
      className={cn(
        "overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm",
        className,
      )}
    >
      <div className="flex items-center gap-2 border-b border-border/50 px-4 py-3">
        <Skeleton className="size-4 rounded" />
        <Skeleton className="h-4 w-32" />
      </div>
      <div className="divide-y divide-border/40">
        {Array.from({ length: rows }, (_, index) => (
          <div key={index} className="flex items-center justify-between gap-4 px-4 py-2.5">
            <Skeleton className="h-3.5 w-24" />
            <Skeleton className="h-3.5 w-32" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Stat strip placeholders (Agents / Connections header stats). */
export function SkeletonStats({ count = 4, className }: { count?: number; className?: string }) {
  return (
    <div
      className={cn(
        "grid grid-cols-2 gap-3",
        count <= 3 ? "md:grid-cols-3" : "md:grid-cols-4",
        className,
      )}
    >
      {Array.from({ length: count }, (_, index) => (
        <div
          key={index}
          className="flex items-center gap-3 rounded-xl border border-border/60 bg-card px-4 py-3 shadow-sm"
        >
          <Skeleton className="size-2 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton className="h-5 w-10" />
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Compact sidebar chat row placeholders. */
export function SkeletonChatRows({ count = 6, className }: { count?: number; className?: string }) {
  return (
    <div className={cn("w-full space-y-0.5", className)} role="status" aria-label="Loading chats">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="flex w-full min-w-0 items-center gap-2.5 rounded-xl px-3 py-2">
          <Skeleton className="size-[26px] shrink-0 rounded-lg" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton className="h-3 w-4/5" />
            <Skeleton className="h-2.5 w-2/5" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Agent console shell placeholder (header + tabs + overview body). */
export function SkeletonPageHeader({ className }: { className?: string }) {
  return (
    <div
      role="status"
      aria-label="Loading agent dashboard"
      className={cn("flex min-h-0 flex-1 flex-col bg-background", className)}
    >
      <header className="shrink-0 border-b border-border/60 bg-card px-6 pt-4 pb-[17px]">
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-4">
            <Skeleton className="size-9 shrink-0 rounded-md" />
            <div className="flex min-w-0 items-center gap-3">
              <Skeleton className="size-[38px] shrink-0 rounded-lg" />
              <div className="space-y-1.5">
                <Skeleton className="h-[22px] w-48" />
                <Skeleton className="h-[18px] w-56" />
              </div>
            </div>
            <Skeleton className="h-6 w-24 shrink-0 rounded-full" />
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Skeleton className="h-8 w-20 rounded-md" />
            <Skeleton className="h-8 w-24 rounded-md" />
          </div>
        </div>
      </header>

      <nav className="flex shrink-0 items-start border-b border-[rgba(219,218,221,0.5)] bg-card px-6">
        {Array.from({ length: 5 }, (_, index) => (
          <div
            key={index}
            className="inline-flex items-center gap-2 border-b-2 border-transparent px-4 pt-3 pb-3.5"
          >
            <Skeleton className="size-3.5 shrink-0 rounded" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </nav>

      <div className="min-h-0 flex-1 overflow-auto">
        <div className="mx-auto max-w-5xl space-y-5 px-8 py-8 pb-10">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
            {Array.from({ length: 6 }, (_, index) => (
              <div
                key={index}
                className="rounded-xl border border-border/60 bg-card px-4 py-3 shadow-sm"
              >
                <Skeleton className="h-7 w-12" />
                <Skeleton className="mt-2 h-3 w-16" />
              </div>
            ))}
          </div>
          <Skeleton className="h-[72px] w-full rounded-xl" />
          <SkeletonConfigCard rows={5} />
          <div className="overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm">
            <div className="flex items-center justify-between border-b border-border/50 px-4 py-3">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-3 w-16" />
            </div>
            <div className="grid gap-3 p-4 sm:grid-cols-2">
              {Array.from({ length: 5 }, (_, index) => (
                <Skeleton key={index} className="h-[52px] w-full rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
